export default {
  title: 'Data Import',
  layout: {
    exit_wizard: 'Exit wizard',
    /** @deprecated — bottom-right skip button removed (Q4). Kept to avoid missing-key warnings. */
    skip: 'Skip ↗',
    /** @deprecated — Resume Later link removed from mobile drawer (Q4). Kept to avoid missing-key warnings. */
    resume_later: 'Resume later',
    open_menu: 'Open menu',
    menu: 'Menu',
    close_menu: 'Close menu',
  },
  controls: {
    back: 'Back',
    skip: 'Skip',
    next: 'Next',
  },
  step_bar: {
    aria_label: 'Onboarding progress',
    go_to_step: 'Go to step {{n}}',
    indicators_aria_label: 'Step indicators',
    step_aria_label: {
      idle: 'Step {{n}}',
      current: 'Step {{n}} (current)',
      completed: 'Step {{n}} (completed)',
    },
    step_of: 'Step {{n}} of {{total}}',
    labels: {
      '1': 'Property',
      '2': 'Pricing',
      '3': 'Rooms',
      '4': 'Customer',
      '5': 'Lease',
      '6': 'Readings',
      '7': 'Invoice',
      '8': 'Payment',
    },
  },
  import_wizard: {
    steps: {
      upload: 'Upload',
      parsing: 'Parsing',
      review: 'Review',
      confirm: 'Import',
      result: 'Done',
    },
    // EP-115 Q-5: frames 1 and 2 draw DIFFERENT titles on the same screen in two
    // states ("import your rooms" before a file is staged, "import property data"
    // after). A title cannot change when a file is dropped, so one had to win.
    // The flow imports a whole property bundle — property, pricing, rooms,
    // customers, leases — so the property wording is the accurate one.
    page_title_upload: 'Import property data',
    page_title_review: 'Review your property data',
  },
  upload: {
    panel_title: 'Upload files',
    panel_subtitle: 'Upload an Excel or CSV file and we fill everything in for you',
    source_system: 'Source System',
    source_label: 'Source System',
    // OnboardingUploadCard keys (task-028)
    upload_card: {
      title: 'Got existing data?',
      subtitle: 'Upload a spreadsheet. We\'ll prefill the next steps.',
      drop_zone: 'Drop .xlsx / .csv here, or browse files',
      source_label: 'Source',
      source_generic: 'Generic',
      source_horganice: 'Horganice',
      background_note: 'Parsing runs in the background. Keep filling out the wizard.',
      uploading: 'Uploading {{filename}}…',
      error_file_too_large: 'File too large. Max 10MB.',
      error_unsupported_format: 'Unsupported format. Use .xlsx or .csv.',
    },
    source_placeholder: 'Select source system',
    source_generic: 'Generic',
    source_generic_desc: 'Any spreadsheet (CSV/Excel)',
    source_horganice: 'Horganice',
    source_horganice_desc: 'Export from Horganice PMS',
    drop_zone_title: 'Click to upload or drag and drop',
    file_type_hint: 'Supported formats: .xlsx, .xls, .csv',
    size_limit: 'Maximum 10 MB per file',
    auto_layout_note: 'We read the layout automatically. No template needed.',
    add_another: 'Add another file',
    // EP-54: migrated from i18next v20-style _plural (silently dead in v25)
    // to v25 _one/_other. Base key removed.
    file_count_one: '{{count}} file selected',
    file_count_other: '{{count}} files selected',
    remove_file: 'Remove',
    start_import: 'Start Import',
    uploading: 'Uploading...',
    errors: {
      source_required: 'Please select a source system',
      files_required: 'Please upload at least one file',
      file_too_large: 'File "{{name}}" exceeds the 10 MB limit',
      invalid_type: 'File "{{name}}" is not a supported format (.xlsx, .xls, or .csv)',
      upload_failed: 'Upload failed. Please try again.',
    },
  },
  parsing: {
    title: 'Parsing Files',
    description: 'Your files are being processed. This may take a moment.',
    progress_label: 'Parsing files...',
    files_label: 'File status',
    error_title: 'Parsing failed',
    error_description: 'One or more files could not be parsed. Please check your files and try again.',
    // B-009 — surfaced when the parse job stalls / never gets picked up.
    timeout_title: 'Parsing is taking longer than expected',
    timeout_description: 'The parser has not responded yet. You can retry or cancel and start over.',
    try_again: 'Try Again',
    file_status: {
      pending: 'Pending',
      parsing: 'Parsing',
      parsed: 'Done',
      failed: 'Failed',
    },
  },
  review: {
    title: 'Review Extracted Data',
    description: 'Review and edit the data we found in your files before importing. Click any cell to edit.',
    dropped_columns_warning_one: '{{count}} column was not recognized and will not be imported: {{columns}}',
    dropped_columns_warning_other: '{{count}} columns were not recognized and will not be imported: {{columns}}',
    confirm_button: 'Confirm & Import',
    no_data_title: 'No data found',
    no_data_description: 'No records were extracted from your files. Please check your files and try again.',
    entity: {
      properties: 'Properties',
      room_types: 'Room Types',
      units: 'Units',
      customers: 'Customers',
      leases: 'Leases',
    },
    columns: {
      name: 'Name',
      address: 'Address',
      property_name: 'Property',
      unit_number: 'Unit No.',
      room_type_name: 'Room Type',
      floor: 'Floor',
      area_sqm: 'Area (sqm)',
      first_name: 'First Name',
      last_name: 'Last Name',
      phone: 'Phone',
      email: 'Email',
      customer_phone: 'Customer Phone',
      start_date: 'Start Date',
      end_date: 'End Date',
      rent_amount: 'Rent',
    },
  },
  confirm: {
    title: 'Confirm Import',
    description: 'Review what will be imported and confirm to start the process.',
    warning: 'This action cannot be undone. All records will be created permanently.',
    summary_title: 'Records to be created',
    start_import: 'Start Import',
    entity: {
      properties: 'properties',
      room_types: 'room types',
      units: 'units',
      customers: 'customers',
      leases: 'leases',
    },
  },
  result: {
    importing_title: 'Importing Data',
    importing_description: 'Please wait while your data is being imported. This may take a moment.',
    success_title: 'Import Complete',
    success_description: 'Your data has been successfully imported into FlowRent.',
    // EP-160: a "Set up for me" support session imports only the building, pricing and rooms tabs.
    part_done_title: 'Building, pricing and rooms imported',
    part_done_description: 'The account owner confirms the tenants and leases tabs later.',
    created_summary: 'Created {{properties}} properties, {{room_types}} room types, {{units}} units, {{customers}} customers, and {{leases}} leases.',
    go_to_properties: 'Go to Properties',
    error_title: 'Import Failed',
    // B-009 — surfaced when the import (executor) job stalls.
    timeout_title: 'Import is taking longer than expected',
    timeout_description: 'The import has not finished yet. You can retry or cancel and start over.',
    try_again: 'Try Again',
  },
  chrome: {
    parsing: 'Parsing… ~{{elapsed}}s',
    parsing_short: 'Parsing…',
    cancel: 'Cancel',
    file_ready: '✓ File ready',
    counts: '{{properties}} property · {{room_types}} room types · {{units}} units · {{customers}} customers · {{leases}} leases',
    hide: 'Hide',
    parse_failed: '✗ Parse failed: {{reason}}',
    parse_timeout: '✗ Parse timed out',
    dismiss: 'Dismiss',
  },
  // EP-56 L4 — replaces deprecated spotlight tips. Rendered as a static
  // inline `<p role="note">` above each step's content. No modal, no
  // dismiss layer, no demand for interaction.
  hint: {
    // EP-114 T012 — split from the single `step_2` key (pre-existing bug: the
    // "on the right" wording has been wrong on mobile since 1.4.2 turned the
    // bill preview into a floating action button, not a side panel). Desktop
    // keeps the original wording verbatim; mobile gets its own copy naming
    // the FAB instead. Rendered conditionally by breakpoint in
    // OnboardingWizardPage.tsx.
    step_3: 'Add room types (Studio, 1BR…) then units inside them. Set opening meter readings inline.',
    step_5: 'Rent is inherited from the selected unit. No need to re-enter it.',
    step_6: 'Today\'s reading minus the opening reading = this month\'s consumption.',
    step_7: 'The bill itemizes rent + utilities + fees using your pricing formula and the readings you just entered. It uses the same engine as the Step 2 preview, which showed sample figures.',
  },
  apply: {
    banner: 'Your file has {{count}} {{entityType}}.',
    entity_property: 'property',
    entity_rooms: 'room types + units',
    entity_customer: 'customers',
    entity_lease: 'leases',
    entity_pricing: 'pricing items',
    button: 'Apply data from file',
  },
  cycle: {
    default_room_type_name: 'Default',
    applying_rooms: 'Creating rooms and units from your file…',
    applying_customers_one: 'Importing {{count}} customer from your file…',
    applying_customers_other: 'Importing {{count}} customers from your file…',
    applying_leases_one: 'Importing {{count}} lease from your file…',
    applying_leases_other: 'Importing {{count}} leases from your file…',
    importing_customers_one: '{{count}} customer ready to import. Review and click Next.',
    importing_customers_other: '{{count}} customers ready to import. Review and click Next.',
    importing_leases_one: '{{count}} lease ready to import. Review and click Next.',
    importing_leases_other: '{{count}} leases ready to import. Review and click Next.',
    imported_customers_one: '✓ {{count}} customer imported. Click Next to move to leases.',
    imported_customers_other: '✓ {{count}} customers imported. Click Next to move to leases.',
    imported_leases_one: '✓ {{count}} lease imported. Click Next to move to meter readings.',
    imported_leases_other: '✓ {{count}} leases imported. Click Next to move to meter readings.',
    batch_skipped_summary_one: '{{skipped}} of {{total}} rows could not be matched to a saved customer/unit and was skipped.',
    batch_skipped_summary_other: '{{skipped}} of {{total}} rows could not be matched to a saved customer/unit and were skipped.',
    batch_no_property: 'No saved property found. Finish Step 1 first.',
    batch_no_readings: 'No readings to apply.',
    batch_readings_applied: '✓ {{count}} meter reading(s) saved.',
    batch_readings_partial:
      '⚠ Only {{saved}} of {{total}} meter reading(s) were saved. Check the readings and try again.',
    list: {
      name: 'Name',
      phone: 'Phone',
      email: 'Email',
      tenant: 'Tenant',
      unit: 'Unit',
      started_at: 'Start date',
      ended_at: 'End date',
      deposit: 'Deposit',
      untitled: '(no name)',
    },
    header: {
      customer: 'Customer {{idx}} of {{total}}',
      lease: 'Lease {{idx}} of {{total}}',
    },
    action: {
      save_next: 'Save & Next',
      skip: 'Skip',
      save_all_remaining: 'Save all remaining',
    },
    confirm_save_all: {
      title: 'Save {{count}} remaining {{entityType}}?',
      body: 'They will be created in sequence without review. If one fails, the batch halts at that row.',
      yes: 'Yes, save all',
    },
    missing_dependency: 'Missing dependency. Pick a {{entityType}} manually.',
    batch_error_prefix_customer: 'Customer {{idx}}: ',
    batch_error_missing_dependency: 'Lease {{idx}}: Missing dependency. Pick a unit/customer manually.',
    batch_error_prefix_lease: 'Lease {{idx}}: ',
  },
  wizard: {
    step_1_title: 'About your property',
    step_1_body: 'A property is the building or complex you manage. Fill in basic info like name, address, and tax ID.',
    // EP-114 S1-13 — helper notice under the billing-day field (also rendered
    // on the standalone property pages, per D-2).
    step_1_billing_day_note: 'Utility rates are configured later, on the Pricing step',
    step_2_title: 'Set up pricing',
    // EP-114 T010 — replaces the retired step-2 explainer-body key (orphaned,
    // never rendered). Rendered by PropertyPricingFields' streamlinedMode info
    // banner, not the wizard chrome. EN is a derived translation authored to
    // mirror the TH copy's nuance (not independently Figma-sourced) — the TH
    // string is the verbatim source, pulled from Figma node 183:1864/183:1868.
    step_2_formula_banner_title: 'Pricing formula',
    step_2_formula_banner_body: 'Pricing formula = how rent + utilities + fees are calculated each month. We\'ve pre-filled a typical Thai apartment setup for you; adjust the rates to match yours. The live preview shows what a bill looks like.',
    // EP-114 T012 (S2-8) — new hint row inside the step-2 pricing panel.
    // Renders identically on mobile and desktop, so the wording must never
    // reference where the preview sits (that varies by breakpoint — a side
    // panel on desktop, a floating button on mobile).
    step_2_rates_hint: 'Edit any rate and the bill preview updates instantly.',
    step_3_title: 'Add rooms and units',
    step_3_body: 'Create your room types (Studio, 1BR…) and individual units inside them. Each unit can have water + electricity meters; record their opening readings inline.',
    step_4_title: 'Add your first customer',
    step_4_body: 'A customer is the person renting from you. Add their name, phone, and email so you can send them invoices and reminders later.',
    step_5_title: 'Create a lease',
    step_5_body: 'A lease ties a customer to a unit for a date range with a deposit. The rent rate is inherited from the unit you set up in Step 3.',
    step_6_title: 'Record meter readings',
    step_6_body: 'Step 3 captured the opening reading. Now record today\'s reading so we know the consumption for this month. The bill will charge the difference.',
    step_7_title: 'Generate the first invoice',
    step_7_body: 'The pricing formula × today\'s consumption + rent + percentage fees → one itemized invoice. Click Generate to see it.',
    step_8_title: 'Record the first payment',
    step_8_body: 'When the tenant pays, record it here against the invoice. Slip verification can be added later from the payment detail page.',
    no_units_warning: 'Add at least one unit before continuing.',
    step_3_select_unit_error: 'Select an available unit to continue.',
    step_saved: 'Saved',
    // EP-158 F1/AC-1: shown on step 5 when the operator went back and picked
    // a different room or tenant, but the lease already has an invoice (or
    // is no longer draft) so the change is refused — editing continues on
    // the original lease.
    step_5_lease_locked_warning: "This lease's room or tenant can't be changed because it already has an invoice. You're editing the original lease.",
    // EP-158 F18/AC-12: step 8 for an invoice with no balance due.
    step_8_invoice_paid: 'This invoice is fully paid.',
    condo_skip_meters_title: 'Meter readings skipped',
    condo_skip_meters_body: 'Condominiums share building meters at the property level, so each unit does not need its own readings. Common area fees are charged from the property\'s CommonAreaFee rows instead.',
    step6_import_title: 'Bulk meter readings from file',
    step6_import_body: 'Review the {{count}} unit meter readings pulled from the uploaded file. These will be saved as the baseline. Next month\'s readings will measure usage from here.',
    step6_import_unmatched: '{{count}} reading(s) referenced units that were not created. Those rows are skipped.',
    step6_col_unit: 'Unit',
    step6_col_water: 'Water (current)',
    step6_col_electricity: 'Electricity (current)',
    step6_group_water: 'Water',
    step6_group_electricity: 'Electricity',
    step6_col_initial: 'Initial',
    step6_col_curr: 'Current',
    step6_apply_button: 'Save all readings',
    dirty_confirm_title: 'Unsaved changes',
    dirty_confirm_message: 'Save before continuing?',
    // EP-69 T1.1 — step-1 adopt/prefill + 2+ choice
    step_1_adopted_notice: 'You already have a property. Review and continue.',
    step_1_has_properties_notice: 'You have existing properties. What would you like to do?',
    step_1_use_existing: 'Use an existing property',
    step_1_create_new: 'Create new',
    step_1_select_property: 'Select property',
    step_1_use_this_property: 'Use This Property',
    // EP-69 T1.2 — step-4 existing-customer picker
    step_4_picker_label: 'Select an existing customer',
    step_4_search_placeholder: 'Search by name or phone…',
    step_4_use_this_customer: 'Use this customer',
    step_4_create_new: '+ Create a new customer',
    step_4_back_to_picker: '← Pick an existing customer',
    step_4_selected_notice: 'Clicking Next will use this customer. No new record will be created.',
    step_4_empty_state: 'No customers yet. Fill in the form below to add the first one.',
    step_4_duplicate_name_warning: 'A customer named "{{name}}" already exists.',
    step_4_duplicate_view_link: 'View',
  },

  // T005 — EP-86 Scenario 1: landing / mode-chooser view (no ?step / ?import / ?mode)
  explore: {
    seeding: {
      title: 'Setting up your demo…',
      creating_property: 'Creating property',
      adding_room_unit: 'Adding room type & unit',
      adding_tenant: 'Adding a sample tenant',
      creating_lease: 'Creating the lease',
      generating_invoice: 'Generating the first invoice',
      finishing: 'Finishing up',
    },
    hub: {
      title: 'Your demo is ready. Now make it yours',
      subtitle:
        'We created one of everything with placeholder values. Open each to refine it for your property (you can also do this later from the dashboard).',
      property: 'Property',
      property_hint: 'Name, address, tax ID & billing day',
      rooms_and_units: 'Rooms & units',
      rooms_and_units_hint: 'Real units & monthly rents',
      tenant: 'Tenant',
      tenant_hint: 'Real name & phone',
      lease: 'Lease',
      lease_hint: 'Start date, deposit & term',
      invoice: 'Invoice',
      invoice_hint: 'Review the generated line items',
      review: 'Review →',
      go_to_dashboard: 'Go to dashboard',
      delete_and_start_fresh: 'Delete & start fresh',
    },
    delete: {
      title: 'Delete sample data?',
      loading: 'Checking the sample data…',
      load_failed: 'Could not check the sample data. Try again.',
      retry: 'Try again',
      counts_lead: 'Only the sample data will be deleted:',
      nothing_to_delete: 'There is no sample data left to delete.',
      own_kept_one: 'Your own data (1 building) stays as it is.',
      own_kept_other: 'Your own data ({{count}} buildings) stays as it is.',
      blocked_title: 'The sample data cannot be deleted yet',
      blocker: 'Cannot delete: {{type}} {{label}} (your own) uses the sample {{depends_type}} {{depends_label}}',
      blockers_more: 'and {{count}} more',
      blocked_hint_lease: 'Move the lease to one of your own rooms first, then try again.',
      blocked_hint: 'Change your own records above so they no longer use sample data, then try again.',
      needs_flowrent: 'Contact FlowRent to delete the sample data.',
      delete_failed: 'The sample data was not deleted. Try again.',
      cancel: 'Cancel',
      close: 'Close',
      delete: 'Delete sample data',
      deleting_title: 'Deleting sample data…',
      deleting_body: 'Removing the sample data only. This may take a moment.',
      tables: {
        properties_one: '{{count}} building',
        properties_other: '{{count}} buildings',
        room_types_one: '{{count}} room type',
        room_types_other: '{{count}} room types',
        units_one: '{{count}} room',
        units_other: '{{count}} rooms',
        meters_one: '{{count}} meter',
        meters_other: '{{count}} meters',
        readings_one: '{{count}} meter reading',
        readings_other: '{{count}} meter readings',
        customers_one: '{{count}} tenant',
        customers_other: '{{count}} tenants',
        leases_one: '{{count}} lease',
        leases_other: '{{count}} leases',
        invoices_one: '{{count}} invoice',
        invoices_other: '{{count}} invoices',
        invoice_items_one: '{{count}} invoice line',
        invoice_items_other: '{{count}} invoice lines',
        payments_one: '{{count}} payment',
        payments_other: '{{count}} payments',
        vendors_one: '{{count}} vendor',
        vendors_other: '{{count}} vendors',
        equipment_one: '{{count}} piece of equipment',
        equipment_other: '{{count}} pieces of equipment',
        maintenance_schedules_one: '{{count}} maintenance schedule',
        maintenance_schedules_other: '{{count}} maintenance schedules',
        work_orders_one: '{{count}} work order',
        work_orders_other: '{{count}} work orders',
        maintenance_requests_one: '{{count}} maintenance request',
        maintenance_requests_other: '{{count}} maintenance requests',
        journal_entries_one: '{{count}} journal entry',
        journal_entries_other: '{{count}} journal entries',
        journal_lines_one: '{{count}} journal line',
        journal_lines_other: '{{count}} journal lines',
        debt_cases_one: '{{count}} debt case',
        debt_cases_other: '{{count}} debt cases',
        lease_signatures_one: '{{count}} lease signature',
        lease_signatures_other: '{{count}} lease signatures',
        work_order_parts_one: '{{count}} work order part',
        work_order_parts_other: '{{count}} work order parts',
        parcels_one: '{{count}} parcel',
        parcels_other: '{{count}} parcels',
        vehicles_one: '{{count}} vehicle',
        vehicles_other: '{{count}} vehicles',
        expenses_one: '{{count}} expense',
        expenses_other: '{{count}} expenses',
        other_one: '{{count}} other record',
        other_other: '{{count}} other records',
      },
      types: {
        property: 'building',
        room_type: 'room type',
        unit: 'room',
        meter: 'meter',
        reading: 'meter reading',
        customer: 'tenant',
        lease: 'lease',
        invoice: 'invoice',
        invoice_item: 'invoice line',
        payment: 'payment',
        vendor: 'vendor',
        equipment: 'equipment',
        maintenance_schedule: 'maintenance schedule',
        work_order: 'work order',
        maintenance_request: 'maintenance request',
        journal_entry: 'journal entry',
        journal_line: 'journal line',
        fixed_asset: 'fixed asset',
        debt_case: 'debt case',
        other: 'record',
      },
    },
    error: {
      title: 'Something went wrong',
      try_again: 'Try again',
      go_to_dashboard: 'Go to dashboard',
    },
    landing: {
      title: 'Welcome to FlowRent',
      subtitle: 'Choose the way to get started that suits you',
      sample: {
        title: 'Try it with sample data first',
        badge: 'RECOMMENDED',
        body: "We'll instantly create a sample property, tenant and invoice so you can click around. Remove it later.",
        meta: 'Takes about 20 seconds',
        button: 'Try now',
      },
      upload: {
        title: 'Import existing data',
        body: 'Already have a room or tenant list? Upload Excel or CSV.',
        meta: 'Supports .xlsx and .csv',
        button: 'Upload file',
      },
      scratch: {
        title: 'Enter it yourself',
        body: 'Set up your property step by step with guidance all the way. About 5 minutes.',
        meta: 'About ~5 min',
        button: 'Start setup',
      },
    },
  },

  // T007 — EP-86 Scenario 1: per-page dismissible coaching banner copy.
  // Shown when ?coach=explore is in the URL and the page key has not been dismissed.
  coach: {
    dismiss: 'Dismiss coaching tip',
    back_to_setup: '← Back to setup',
    property: {
      title: 'This is sample data.',
      body: 'Rename the property and set your real address, tax ID and billing day.',
    },
    rooms: {
      title: 'These are sample rooms.',
      body: 'Check the room numbers and rents match yours.',
    },
    tenant: {
      title: 'This is a sample tenant.',
      body: 'Update the name and phone number with real details, or delete this tenant and add your own.',
    },
    lease: {
      title: 'This is a sample lease.',
      body: 'Adjust the start and end dates, deposit amount and rent to match your actual agreement.',
    },
    invoice: {
      title: 'This is a sample invoice.',
      body: 'Review the formula-generated line items and confirm the amounts match your pricing setup.',
    },
  },

  // T020 — EP-86 Scenario 3: tabbed review shell (ImportReviewTabs).
  // Tab labels, confirm-per-tab button, final import button, column headers,
  // and dependency-link validation messages.
  import: {
    tabs: {
      aria_label: 'Review tabs',
      property: 'Property',
      pricing: 'Pricing',
      rooms: 'Rooms & Units',
      customers: 'Customers',
      leases: 'Leases',
      confirmed: 'confirmed',
    },
    confirm_tab: 'Confirm {{tab}}',
    // The Pricing tab confirms a FORMULA, not a price, so it does not use the
    // `confirm_tab` interpolation above — "confirm pricing" reads like a
    // single amount is being agreed. Owner wording, 2026-08-05.
    confirm_pricing: 'Confirm pricing formula',
    import_everything: 'Import everything',
    import_gate: 'Confirm every tab before importing',
    review_panel_title: 'Confirm your property data',
    review_panel_subtitle: 'To bring your existing property data into FlowRent',
    columns: {
      // Property tab
      name: 'Name',
      address: 'Address',
      tax_id: 'Tax ID',
      branch_number: 'Branch',
      phone: 'Phone',
      email: 'Email',
      location_url: 'Map URL',
      // Rooms & Units tab
      unit_number: 'Unit No.',
      room_type_name: 'Room Type',
      price: 'Price',
      area_sqm: 'Area (sqm)',
      // Customers tab
      first_name: 'First Name',
      last_name: 'Last Name',
      // Leases tab
      tenant_name: 'Tenant',
      started_at: 'Start',
      ended_at: 'End',
      rent_amount: 'Rent',
      deposit_amount: 'Deposit',
      move_in_fee_amount: 'Move-in fee',
      keycard_deposit_amount: 'Key card deposit',
    },
    // EP-158 D2 (2026-10-09): an imported lease with status active is
    // recorded as its deposit fully collected automatically (decided from
    // the lease's own status, not a checkbox), replacing the removed
    // file-level checkbox and per-row column.
    leases_note: 'A lease that has started and not ended is recorded with its deposit fully collected.',
    validation: {
      unit_not_found: 'Unit not in Rooms & Units',
      tenant_not_found: 'Tenant not in Customers',
    },
    // Dropdown placeholders for linking a lease to a unit / customer.
    link: {
      source_tab_empty: 'That tab has no rows yet — fill it in first, then come back to link.',
      no_match: 'No match in this file.',
      select_unit: 'Select a unit…',
      select_customer: 'Select a customer…',
    },
    // T021 — multi-property loop copy
    property_k_of_n: 'Property {{k}} of {{n}}',
    n_more_waiting: '{{n}} more waiting',
    continue_with_next: 'Continue with next property',
    finish: 'Finish',
    all_imported_one: 'Property imported',
    all_imported_other: 'All {{count}} properties imported',
    skipped_leases: {
      title_one: '{{count}} lease row was not imported',
      title_other: '{{count}} lease rows were not imported',
      description:
        'These rows are missing what the importer needs to create a lease. Fix them in your spreadsheet and import again, or add the leases by hand.',
      unit: 'Unit',
      tenant: 'Tenant',
      reason: 'Reason',
      unknown_tenant: 'Not given',
      reasons: {
        unit_not_found: 'Unit not found in the file',
        no_phone: 'No phone number to match a tenant',
        tenant_not_found: "Phone number doesn't match any tenant",
      },
    },
    reassigned_building_rows: {
      title_one: '{{count}} row was assigned automatically',
      title_other: '{{count}} rows were assigned automatically',
      description:
        'These rows had no building listed in the file, so the importer placed them in the first building. Check that each one belongs there.',
      unit: 'Unit',
      tenant: 'Tenant',
      assigned_to: 'Assigned to',
      unknown_tenant: 'Not given',
    },
    // T149-078 addition (MON-7 C import UI): leases whose held-before-FlowRent
    // deposit posting failed during import (the lease itself imported fine).
    // The heading is the owner's exact copy, given flat with no count.
    deposit_held_failed_rows: {
      title: 'Deposit held not recorded',
      description:
        'These leases imported, but recording their deposit as already held before FlowRent failed. Record it by hand from each lease.',
      property: 'Property',
      unit: 'Unit',
      customer: 'Customer',
      reason: 'Reason',
      unknown_customer: 'Not given',
      action_link: 'Go to lease',
    },
    go_to_properties: 'Go to Properties',
    grid: {
      aria_label: 'Spreadsheet grid',
      add_row: 'Add row',
      delete_row: 'Delete row',
      row_count_one: '{{count}} row',
      row_count_other: '{{count}} rows',
    },
  },

  // EP-56 L2 — sandboxed sample-data demo accessed via ?mode=sample
  sample_mode: {
    see_sample_button: 'See a sample invoice (no data needed)',
    title: 'Sample mode: nothing is saved',
    body: 'These numbers come from a typical 1-bed studio in Bangkok. Nothing here touches your account.',
    start_fresh: 'Start fresh',
    invoice_title: 'Sample invoice for this month',
    invoice_subtitle: 'Rent ฿5,000 + Water 10u + Electricity 50u + 3% common fee = ฿5,695.90',
    col_description: 'Description',
    col_qty: 'Qty',
    col_rate: 'Unit Price',
    col_amount: 'Amount',
    download_csv: 'Download CSV',
  },

  // EP-160 S8 the "where did you get stuck?" question when onboarding stops part way
  blocker: {
    title: 'Where did you get stuck?',
    intro: 'Tell us a little so we can make setup easier to finish.',
    codes: {
      no_time: 'No time',
      too_hard: 'Too hard',
      other_system: 'My data is in another system',
      waiting_line: 'Waiting to set up LINE',
      price: 'Price',
      other: 'Other',
    },
    note_label: 'Tell us more (optional)',
    skip: 'Skip',
    send: 'Send',
    error: 'Could not send. Please try again.',
  },
}
